import React, { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Delete, Edit } from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import { Contrato } from '../../../api'
import { EnumTipoNotificacion } from '../../../api/oData/enums/enumTipoNotificacion'
import { DataTableErrorHandler } from '../../../components/errors/scopes/dataTable/dataTableError'
import { getUserPermissions, hasPermission } from '../../../helpers/permissionsHelper'
import { getEmailRegex } from '../../../helpers/regexHelper'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  useHttpError
} from '../../../hooks'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Pages, Resources } from '../../../model/RBAC/constants'
import {
  DataTableFilter,
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../redux'
import { fetchNotificationTypes } from '../../clients/client.service'
import { ContactInfoModelV3 } from '../../../model/contactInfo'
import {
  completeDataTableActions,
  contactInfoRowMapper,
  contractsMapperToDropDown
} from '../contactsInfoV3.mapper'
import {
  deleteContactInfo,
  getContactsInfoBySubjectId,
  patchContactInfo,
  postContactInfo
} from '../../../services/contactsInfoV3.service'
import { fetchContractsBySubject } from '../contactsInfo.service'
import { getCUPSBySubjectId } from '../../../services/subjects.service'

export interface ContactsInfoTableProps {
  clientID: number
  dataTableFilter: DataTableFilter | undefined
  dataTableSearch: string | undefined
  onFetching: (isFetching: boolean) => void
  setNeedUpdateClient: () => void
}

// If contacts are added, edited or removed, we call setNeedUpdateClient,
// that will set a state in contactsInfo and will trigger (or not) a new fetch before going back to client File.
// This way we don't (unnecesarily) fetch clientData every time we change a contact info.

export const ContactsInfoTable: React.FC<ContactsInfoTableProps> = props => {
  const [t] = useTranslation(['contactsInfo', 'enums'])
  let history = useHistory()
  const { info, error } = useNotify()
  const { errorProps } = useHttpError()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [contactsInfo, setContactsInfo] = useState<ContactInfoModelV3[] | undefined>(undefined)
  const [notificationTypes, setNotificationTypes] = useState<SelectOption[]>([])
  const [contractsOptions, setContractsOptions] = useState<SelectOption[]>([])
  const [cupsOptions, setCupsOptions] = useState<SelectOption[]>([])

  const {
    openForm,
    updateFormDefinition,
    updateFormData,
    closeForm,
    setFormWaiting,
    formProps,
    CustomFormDialog
  } = useFormDialog()
  const [mailRequired, setMailRequired] = useState<boolean>(false)
  const [mailIsValid, setMailIsValid] = useState<boolean>(false)
  //Hooks Region Starts
  useEffect(() => {
    props.onFetching(true)

    const getNotificationTypes = async () => {
      await fetchNotificationTypes().then(response => {
        setNotificationTypes(response.data)
      })
    }

    const getContractsForSubject = async (subjectID: number) => {
      await fetchContractsBySubject(subjectID).then(response => {
        if (response.success) {
          const contracts: Contrato[] = response.data
          if (contracts && contracts.length > 0) {
            setContractsOptions(contractsMapperToDropDown(contracts))
          }
        }
      })
    }

    const getCUPSForSubject = async (subjectID: number) => {
      await getCUPSBySubjectId(subjectID).then(response => {
        if (response.success) {
          const cups: Record<string, string> = (response.data ?? {}) as Record<string, string>
          if (Object.keys(cups).length > 0) {
            setCupsOptions(
              Object.entries(cups).map(([key, value]) => ({
                label: value,
                value: key
              }))
            )
          }
        }
      })
    }

    getNotificationTypes()
    getContractsForSubject(props.clientID)
    getCUPSForSubject(props.clientID)
    props.onFetching(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchContactsInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientID])
  //Hooks Region Ends

  // Functions Starts

  const validateContactFields = (row: ContactInfoModelV3): boolean => {
    const hasContactName = !!row.contactName?.trim()
    const hasEmail = !!row.email?.trim()
    const hasPhoneNumber = !!row.phoneNumber?.trim()

    return hasContactName || hasEmail || hasPhoneNumber
  }

  const onRowInsert = async (row: ContactInfoModelV3) => {
    if (!validateContactFields(row)) {
      error(
        t(
          'contactsInfo:dataTable.notifications.input.missingContactFields'
        )
      )
      return false
    }

    props.onFetching(true)
    row.clientId = props.clientID
    await postContactInfo(contactInfoRowMapper(row)).then(response => {
      if (response.success) {
        fetchContactsInfo()
        props.setNeedUpdateClient()
        closeForm()
      }
      props.onFetching(false)
    })
    return true
  }

  const onRowEdit = async (row: ContactInfoModelV3) => {
    if (!validateContactFields(row)) {
      error(
        t(
          'contactsInfo:dataTable.notifications.input.missingContactFields'
        )
      )
      return false
    }

    props.onFetching(true)
    row.clientId = props.clientID
    await patchContactInfo(contactInfoRowMapper(row)).then(response => {
      if (response.success) {
        fetchContactsInfo()
        props.setNeedUpdateClient()
        closeForm()
        return true
      } else {
        return false
      }
    })
    props.onFetching(false)
    closeForm()
    return true
  }

  const onRowDelete = async (row: ContactInfoModelV3) => {
    getConfirmation(ConfirmationDialogs.DELETE_CONTACT).then(async () => {
      props.onFetching(true)
      await deleteContactInfo(row.id!).then(response => {
        if (response.success) {
          props.setNeedUpdateClient()
          info(t('contactsInfo:dataTable.notifications.success.delete'))
          fetchContactsInfo()
        } else {
          error(t('contactsInfo:dataTable.notifications.error.delete'))
        }
      })
      props.onFetching(false)
      closeConfirmation()
    })
  }

  const fetchContactsInfo = async (): Promise<boolean> => {
    props.onFetching(true)
    await getContactsInfoBySubjectId(props.clientID).then(response => {
      if (response.success) {
        setContactsInfo(
          response.data?.map((item: ContactInfoModelV3) => {
            return completeDataTableActions(item)
          })
        )
        props.onFetching(false)
        return true
      }
    })
    props.onFetching(false)
    return false
  }

  // Functions Ends

  // DataTable Config Starts

  const contactEditDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'contactName',
                label: t('contactsInfo:dataTable.columns.contactName'),
                type: DataType.TEXT,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    contactName: value
                  })
                }
              },
              {
                name: 'email',
                label: t('contactsInfo:dataTable.columns.email'),
                type: DataType.TEXT,
                regexp: getEmailRegex(),
                errorText: t('contactsInfo:dataTable.notifications.input.invalidEmail'),
                required: mailRequired,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    email: value
                  })
                }
              },
              {
                name: 'phoneNumber',
                label: t('contactsInfo:dataTable.columns.phoneNumber'),
                type: DataType.PHONE,
                maxLength: 16,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    phoneNumber: value
                  })
                }
              },
              {
                name: 'comments',
                label: t('contactsInfo:dataTable.columns.comments'),
                type: DataType.TEXT,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    comments: value
                  })
                }
              },
              {
                name: 'notificationTypes',
                label: t('contactsInfo:dataTable.columns.notificationType'),
                type: DataType.MULTISELECT,
                selectOptions: notificationTypes,
                clearable: true,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    notificationTypes: value
                  })
                  const valueArray = Array.isArray(value) ? value : [value]
                  if (
                    valueArray.some(
                      val =>
                        +val === EnumTipoNotificacion.EnvioCorreoElectronico ||
                        +val === EnumTipoNotificacion.Ambos
                    )
                  ) {
                    setMailRequired(true)
                  } else {
                    setMailRequired(false)
                  }
                }
              },
              {
                name: 'contractIds',
                label: t('contactsInfo:dataTable.columns.contract'),
                type: DataType.MULTISELECT,
                selectOptions: contractsOptions,
                clearable: true,
                required: false,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    contractIds: value
                  })
                }
              },
              {
                name: 'cupsIds',
                label: t('contactsInfo:dataTable.columns.cups'),
                type: DataType.MULTISELECT,
                selectOptions: cupsOptions,
                clearable: true,
                required: false,
                onChange: (value, prevState) => {
                  updateFormData({
                    ...prevState,
                    cupsIds: value
                  })
                }
              }
            ]
          }
        ]
      }
    }, [contractsOptions, notificationTypes, mailRequired, cupsOptions])

  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'contactName',
      label: t('contactsInfo:dataTable.columns.contactName'),
      type: DataType.TEXT
    },
    {
      name: 'email',
      label: t('contactsInfo:dataTable.columns.email'),
      type: DataType.TEXT
    },
    {
      name: 'phoneNumber',
      label: t('contactsInfo:dataTable.columns.phoneNumber'),
      type: DataType.TEXT,
      maxLength: 16
    },
    {
      name: 'comments',
      label: t('contactsInfo:dataTable.columns.comments'),
      type: DataType.TEXT
    },
    {
      name: 'notificationTypes',
      label: t('contactsInfo:dataTable.columns.notificationType'),
      type: DataType.MULTISELECT,
      selectOptions: notificationTypes
    },
    {
      name: 'contractIds',
      label: t('contactsInfo:dataTable.columns.contract'),
      type: DataType.MULTISELECT,
      selectOptions: contractsOptions,
      required: false
    },
    {
      name: 'cupsIds',
      label: t('contactsInfo:dataTable.columns.cups'),
      type: DataType.MULTISELECT,
      selectOptions: cupsOptions,
      required: false
    }
  ]

  const handleEditContact = (contact: ContactInfoModelV3) => {
    const types = contact?.notificationTypes || []
    if (
      types.some(
        val =>
          +val === EnumTipoNotificacion.EnvioCorreoElectronico ||
          +val === EnumTipoNotificacion.Ambos
      )
    ) {
      setMailRequired(true)
    } else {
      setMailRequired(false)
    }

    openForm({
      definition: contactEditDefinition,
      title: t('contactsInfo:dataTable.customActions.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: contact => {
        contact ? onRowEdit(contact) : closeForm()
      },
      inputData: {
        ...contact,
        // API may return null for optional fields; sanitize to avoid crashes
        // in SELECT/MULTISELECT components that call .match() on the value
        email: contact.email ?? undefined,
        phoneNumber: contact.phoneNumber ?? undefined,
        comments: contact.comments ?? undefined,
        phoneCode: contact.phoneCode ?? undefined,
        notificationTypes: contact.notificationTypes ?? [],
        contractIds: contact.contractIds ?? [],
        cupsIds: contact.cupsIds ?? []
      }
    })
  }

  const handleAddContact = () => {
    setMailRequired(false)
    openForm({
      definition: contactEditDefinition,
      title: t('contactsInfo:dataTable.customActions.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: contact => {
        contact ? onRowInsert(contact) : closeForm()
      }
    })
  }
  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.ADVANCED,
    download: true,
    print: false,
    selectableRows: false,
    canEdit: false,
    canDelete: false,
    canInsert: hasPermission(Resources.Client, [UserPermission.EDIT]),
    customInsertEditModal: true,
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('contactsInfo:dataTable.customActions.edit.title'),
        onClick: (row: ContactInfoModelV3) => handleEditContact(row),
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        visibleIf: 'canDelete',
        tooltip: t('contactsInfo:dataTable.customActions.delete.title'),
        userPermission: UserPermission.EDIT,
        onClick: onRowDelete
      }
    ],
    analytics: {
      customer: getCustomerDomain(),
      view: t('contactsInfo:dataTable.analytics')
    },
    onRowInsert: async () => {
      handleAddContact()
      return true
    },
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  useEffect(() => {
    updateFormDefinition(contactEditDefinition)
  }, [contactEditDefinition])

  // DataTable Config Ends

  return (
    <>
      <DataTable
        data={contactsInfo}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Client)}
        appliedFilter={props.dataTableFilter}
        appliedSearch={props.dataTableSearch}
      />
      <CustomFormDialog {...formProps} />
    </>
  )
}
